import { useQuery } from '@tanstack/react-query'
import { getApiBaseUrl } from '@/lib/api'
import { getAuthHeaders } from '@/services/api'
import type { BrandMentionsState } from '@/types/brandMentions'

export async function fetchBrandMentions(
	programId: string,
	limit?: number
): Promise<BrandMentionsState> {
	const headers = await getAuthHeaders()
	const params = new URLSearchParams({ programId })
	if (limit) params.set('limit', String(limit))
	const response = await fetch(
		`${getApiBaseUrl()}/api/brand-mentions?${params}`,
		{ headers }
	)
	const data = await response.json()
	if (!response.ok) {
		throw new Error(data.error || 'Unable to load brand mentions.')
	}
	return data
}

export async function searchBrandMentions(
	programId: string,
	action: 'start' | 'continue',
	runId: string
): Promise<BrandMentionsState> {
	const headers = await getAuthHeaders()
	const response = await fetch(`${getApiBaseUrl()}/api/brand-mentions`, {
		method: 'POST',
		headers,
		body: JSON.stringify({ programId, action, runId }),
	})
	const data = await response.json()
	if (!response.ok) {
		throw new Error(data.error || 'Unable to search brand mentions.')
	}
	return data
}

export function useBrandMentions(programId: string | null, limit?: number) {
	return useQuery<BrandMentionsState, Error>({
		queryKey: ['brandMentions', programId, limit ?? 'all'],
		enabled: Boolean(programId),
		queryFn: () => fetchBrandMentions(programId as string, limit),
		staleTime: 60 * 1000,
		refetchOnWindowFocus: false,
	})
}
